import {Button, Tooltip} from '@mantine/core';
import type {FC, MouseEvent} from 'react';
import {isReleasedEntryManagement} from '../../../config/unReleasePage';
import {useAuth} from '../../../core/usecases/auth/AuthContext';
import {ENTRY_SOURCE_TYPE_EXTERNAL_ENTRY_FORM} from '../../../features/entryManagements/constants/entrySourceTypes';
import {cn} from '../../../lib/classname';
import {appendQueryParam} from '../../services/appendQueryParam';
import {checkExternalEntryUrl} from '../../services/checkExternalEntryUrl';
import {useCreateEntryManagementIfNotExists} from '../../services/useCreateEntryManagementIfNotExists';
import {CommonLink} from '../CommonLink';

export const ENTRY_BUTTON_ID = {
  RECRUITING_INFO: 'recruiting_info_entry_button',
  COMPANY_ENTRY_MAIN: 'company_entry_entry_button_main',
  COMPANY_ENTRY_SP: 'company_entry_entry_button_sp',
} as const;

export const ENTRY_BUTTON_TEXT_SIZE = {
  SM: 'sm',
  BASE: 'base',
  MD: 'md',
  LG: 'lg',
} as const;

// 質問入力なしでエントリーできる募集の見せ方。複数箇所で同じ訴求をするため揃える
export const QUICK_ENTRY_BUTTON = {
  label: '1クリックで応募',
  tooltip:
    'エントリーフォームの質問入力なしで完了。プロフィール情報だけで応募できます。',
  gradientClassName:
    'bg-gradient-to-r from-deadline-notification to-category-12',
} as const;

type EntryButtonId = (typeof ENTRY_BUTTON_ID)[keyof typeof ENTRY_BUTTON_ID];
type EntryButtonSize =
  (typeof ENTRY_BUTTON_TEXT_SIZE)[keyof typeof ENTRY_BUTTON_TEXT_SIZE];

const SIZE_STYLES: Record<EntryButtonSize, string> = {
  [ENTRY_BUTTON_TEXT_SIZE.SM]: 'text-sm',
  [ENTRY_BUTTON_TEXT_SIZE.BASE]: 'text-base',
  [ENTRY_BUTTON_TEXT_SIZE.MD]: 'text-md',
  [ENTRY_BUTTON_TEXT_SIZE.LG]: 'text-lg',
};

type Props = {
  entryUrl: string;
  sourceUrl?: string;
  id?: EntryButtonId;
  customId?: string;
  rel?: string;
  size?: EntryButtonSize;
  className?: string;
  dataGtmClick?: string;
  dataGtmContent?: string;
  withShadow?: boolean;
  /** 選考予定を追加するための企業ID（外部サイトへの遷移時に使用） */
  companyId?: number;
  /** 選考予定を追加するための企業名（外部サイトへの遷移時に使用） */
  companyName?: string;
  /** 募集情報のタイトル（選考管理のstepNameとして使用） */
  entryTitle?: string;
  /** 募集情報の締切日（選考管理のstepのdeadlineとして使用） */
  deadline?: string;
  /** ボタンに表示するテキスト（未指定の場合は「エントリー」） */
  label?: string;
  /** hover 時に表示する補足説明 */
  tooltip?: string;
  /** グラデーションの Tailwind クラス（未指定の場合はデフォルトの bg-gradient-to-r from-error to-sub） */
  gradientClassName?: string;
  /**
   * 未ログイン時にリンク遷移の代わりに行う処理（その場で登録モーダルを開くなど）。
   * true を返したときだけリンク遷移を止める。false ならそのままリンク先へ遷移する
   */
  onUnauthenticatedClick?: () => boolean;
};

export const GradientEntryButton: FC<Props> = ({
  entryUrl,
  sourceUrl,
  id,
  customId,
  rel = 'noopener noreferrer',
  size = ENTRY_BUTTON_TEXT_SIZE.SM,
  className,
  dataGtmClick,
  dataGtmContent,
  withShadow = false,
  companyId,
  companyName,
  entryTitle,
  deadline,
  label,
  tooltip,
  gradientClassName = 'bg-gradient-to-r from-error to-sub',
  onUnauthenticatedClick,
}) => {
  const {status} = useAuth();
  const isAuthenticated = status === 'authenticated';
  const isUnauthenticated = status === 'unauthenticated';

  const href = sourceUrl
    ? appendQueryParam(entryUrl, 'source_url', sourceUrl)
    : entryUrl;

  // 選考管理機能が有効かつ必要なパラメータが揃っている場合のみクエリを実行
  const hasRequiredParams =
    companyId !== undefined && companyName !== undefined;
  const shouldEnableEntryManagement =
    isReleasedEntryManagement() && hasRequiredParams && isAuthenticated;

  const {createIfNotExists} = useCreateEntryManagementIfNotExists({
    enabled: shouldEnableEntryManagement,
  });

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // 修飾キー付きのクリックは別タブで開く操作なので、リンクの動作をそのまま通す
    const isPlainLeftClick =
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey;
    if (
      isUnauthenticated &&
      onUnauthenticatedClick &&
      isPlainLeftClick &&
      onUnauthenticatedClick()
    ) {
      event.preventDefault();
      return;
    }

    // 未認証またはパラメータ不足の場合は処理をスキップ
    if (!isAuthenticated || !hasRequiredParams) {
      return;
    }

    // 外部サイトへの遷移時のみ選考予定を追加
    const {isExternal} = checkExternalEntryUrl(href);
    if (isReleasedEntryManagement() && isExternal) {
      createIfNotExists({
        companyId,
        companyName,
        entryTitle,
        deadline,
        entrySourceType: ENTRY_SOURCE_TYPE_EXTERNAL_ENTRY_FORM,
      });
    }
  };

  const button = (
    <Button
      component={CommonLink}
      id={customId ?? id}
      href={href}
      target="_blank"
      rel={rel}
      data-gtm-click={dataGtmClick}
      data-gtm-content={dataGtmContent}
      onClick={handleClick}
      className={cn(
        'font-bold rounded-[8px] hover:opacity-80 flex items-center justify-center border-0',
        gradientClassName,
        SIZE_STYLES[size],
        withShadow && 'shadow-card',
        className,
      )}
    >
      <span className="text-white font-bold leading text-center">
        {label ?? 'エントリー'}
      </span>
    </Button>
  );

  if (!tooltip) return button;

  return (
    <Tooltip label={tooltip} withArrow multiline width={220} position="top">
      {button}
    </Tooltip>
  );
};
